import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CreditCard, Loader2, Receipt, AlertCircle, Check, X,
  Clock, DollarSign, Calendar, ChevronRight, ArrowLeft,
  FileText, Send
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BillingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [refundRequests, setRefundRequests] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('invoices');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchBillingData();
    }
  }, [isAuthenticated]);

  const getAuthHeaders = () => {
    const sessionToken = localStorage.getItem('session_token');
    return sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
  };

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      
      const [subRes, invRes, refRes] = await Promise.all([
        fetch(`${API_URL}/api/profile/billing/subscription`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/profile/billing/invoices`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/profile/billing/refund-requests`, { headers, credentials: 'include' })
      ]);
      
      if (subRes.ok) {
        const subData = await subRes.json();
        setSubscription(subData);
      }
      
      if (invRes.ok) {
        const invData = await invRes.json();
        setInvoices(invData.invoices || []);
      }
      
      if (refRes.ok) {
        const refData = await refRes.json();
        setRefundRequests(refData.refund_requests || []);
      }
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRefund = async () => {
    if (!selectedInvoice || !refundReason.trim()) return;
    
    setSubmitting(true);
    setMessage({ type: '', text: '' });
    
    try {
      const headers = {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      };
      
      const res = await fetch(`${API_URL}/api/profile/billing/refund-requests`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          stripe_invoice_id: selectedInvoice.stripe_invoice_id,
          reason: refundReason.trim()
        })
      });
      
      if (res.ok) {
        setMessage({ type: 'success', text: 'Refund request submitted successfully!' });
        setSelectedInvoice(null);
        setRefundReason('');
        fetchBillingData();
      } else {
        const error = await res.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to submit refund request' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to submit refund request' });
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      approved: 'bg-green-500/20 text-green-400 border-green-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
      paid: 'bg-green-500/20 text-green-400 border-green-500/30'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${styles[status] || styles.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050810]">
        <Header />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="h-8 w-8 text-[#3B82F6] animate-spin" />
          <span className="ml-3 text-gray-400">Loading billing information...</span>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050810]">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/profile')}
          className="text-gray-400 hover:text-white mb-6"
          data-testid="back-to-profile"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Profile
        </Button>

        {/* Page Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 bg-[#3B82F6]/20 rounded-xl">
            <CreditCard className="h-6 w-6 text-[#3B82F6]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Billing & Subscription</h1>
            <p className="text-gray-400 text-sm">Manage your subscription and view invoices</p>
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-xl border flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-green-500/10 border-green-500/30 text-green-400'
              : 'bg-red-500/10 border-red-500/30 text-red-400'
          }`}>
            {message.type === 'success' ? <Check className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
            {message.text}
          </div>
        )}

        {/* Subscription Card */}
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Current Subscription</h2>
            {subscription?.is_active ? (
              <span className="px-3 py-1 bg-green-500/20 text-green-400 text-sm font-medium rounded-full border border-green-500/30">
                Active
              </span>
            ) : (
              <span className="px-3 py-1 bg-gray-500/20 text-gray-400 text-sm font-medium rounded-full border border-gray-500/30">
                Inactive
              </span>
            )}
          </div>
          
          {subscription?.is_active ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-[#1e2a45]/50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                  <DollarSign className="h-4 w-4" />
                  Plan
                </div>
                <span className="text-white font-semibold">{subscription.tier_label}</span>
              </div>
              <div className="bg-[#1e2a45]/50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                  <Calendar className="h-4 w-4" />
                  Period End
                </div>
                <span className="text-white font-semibold">
                  {subscription.current_period_end 
                    ? new Date(subscription.current_period_end).toLocaleDateString()
                    : 'N/A'}
                </span>
              </div>
              <div className="bg-[#1e2a45]/50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                  <Clock className="h-4 w-4" />
                  Days Remaining
                </div>
                <span className="text-white font-semibold">{subscription.days_remaining}</span>
              </div>
              <div className="bg-[#1e2a45]/50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                  <CreditCard className="h-4 w-4" />
                  Status
                </div>
                <span className="text-green-400 font-semibold capitalize">{subscription.status}</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-400 mb-4">You don't have an active subscription</p>
              <Button
                onClick={() => navigate('/')}
                className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
              >
                Subscribe Now
              </Button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'invoices' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('invoices')}
            className={activeTab === 'invoices' 
              ? 'bg-[#3B82F6] text-white' 
              : 'text-gray-400 hover:text-white'}
            data-testid="invoices-tab"
          >
            <Receipt className="h-4 w-4 mr-2" />
            Invoices
          </Button>
          <Button
            variant={activeTab === 'refunds' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('refunds')}
            className={activeTab === 'refunds' 
              ? 'bg-[#3B82F6] text-white' 
              : 'text-gray-400 hover:text-white'}
            data-testid="refunds-tab"
          >
            <FileText className="h-4 w-4 mr-2" />
            Refund Requests
            {refundRequests.filter(r => r.status === 'pending').length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                {refundRequests.filter(r => r.status === 'pending').length}
              </span>
            )}
          </Button>
        </div>

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] overflow-hidden">
            <div className="p-4 border-b border-[#1e2a45]">
              <h3 className="text-white font-medium">Payment History</h3>
              <p className="text-gray-500 text-sm">View your invoices and request refunds</p>
            </div>
            
            {invoices.length === 0 ? (
              <div className="p-8 text-center">
                <Receipt className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No invoices found</p>
              </div>
            ) : (
              <div className="divide-y divide-[#1e2a45]">
                {invoices.map((invoice) => (
                  <div 
                    key={invoice.id}
                    className="p-4 hover:bg-[#1e2a45]/30 transition-colors"
                    data-testid={`invoice-${invoice.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-white font-medium">{invoice.amount_display}</span>
                          {getStatusBadge(invoice.status)}
                          <span className="text-gray-500 text-sm">{invoice.tier}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-400">
                          <span>
                            <Calendar className="h-3 w-3 inline mr-1" />
                            {invoice.paid_at ? new Date(invoice.paid_at).toLocaleDateString() : 'N/A'}
                          </span>
                          <span>
                            Period: {invoice.period_start ? new Date(invoice.period_start).toLocaleDateString() : 'N/A'} - {invoice.period_end ? new Date(invoice.period_end).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {invoice.can_request_refund ? (
                          <Button
                            size="sm"
                            onClick={() => setSelectedInvoice(invoice)}
                            className="bg-[#F97316] hover:bg-[#EA580C] text-white"
                            data-testid={`refund-btn-${invoice.id}`}
                          >
                            Request Refund
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        ) : invoice.refund_reason ? (
                          <span className="text-gray-500 text-xs max-w-[200px] truncate" title={invoice.refund_reason}>
                            {invoice.refund_reason}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Refund Requests Tab */}
        {activeTab === 'refunds' && (
          <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] overflow-hidden">
            <div className="p-4 border-b border-[#1e2a45]">
              <h3 className="text-white font-medium">Your Refund Requests</h3>
              <p className="text-gray-500 text-sm">Track the status of your refund requests</p>
            </div>
            
            {refundRequests.length === 0 ? (
              <div className="p-8 text-center">
                <FileText className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No refund requests</p>
              </div>
            ) : (
              <div className="divide-y divide-[#1e2a45]">
                {refundRequests.map((req) => (
                  <div 
                    key={req.id}
                    className="p-4"
                    data-testid={`refund-request-${req.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <span className="text-white font-medium">
                            Requested: ${req.requested_refund_cents / 100}
                          </span>
                          {getStatusBadge(req.status)}
                        </div>
                        <span className="text-gray-500 text-sm">
                          {new Date(req.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {req.status === 'approved' && req.approved_refund_cents && (
                        <div className="text-right">
                          <span className="text-green-400 font-semibold">
                            Refunded: ${req.approved_refund_cents / 100}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="bg-[#1e2a45]/50 rounded-lg p-3">
                      <p className="text-gray-400 text-sm">
                        <strong className="text-gray-300">Reason:</strong> {req.reason}
                      </p>
                      {req.admin_notes && (
                        <p className="text-gray-400 text-sm mt-2">
                          <strong className="text-gray-300">Admin Notes:</strong> {req.admin_notes}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Policy Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Button
            variant="link"
            onClick={() => navigate('/refund-policy')}
            className="text-gray-400 hover:text-[#3B82F6]"
          >
            Refund Policy
          </Button>
          <Button
            variant="link"
            onClick={() => navigate('/terms')}
            className="text-gray-400 hover:text-[#3B82F6]"
          >
            Terms of Service
          </Button>
          <Button
            variant="link"
            onClick={() => navigate('/privacy')}
            className="text-gray-400 hover:text-[#3B82F6]"
          >
            Privacy Policy
          </Button>
        </div>
      </div>

      <Footer />

      {/* Refund Request Modal */}
      <Dialog open={!!selectedInvoice} onOpenChange={() => setSelectedInvoice(null)}>
        <DialogContent className="sm:max-w-lg bg-[#050810] border-[#1e2a45]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <Send className="h-5 w-5 text-[#F97316]" />
              Request Refund
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Submit a prorated refund request for this invoice
            </DialogDescription>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4 mt-4">
              {/* Invoice Summary */}
              <div className="bg-[#1e2a45]/50 rounded-xl p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-gray-500 text-sm">Original Amount</span>
                    <p className="text-white font-semibold">{selectedInvoice.amount_display}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Prorated Refund</span>
                    <p className="text-green-400 font-semibold">{selectedInvoice.prorated_refund_display}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Plan</span>
                    <p className="text-white">{selectedInvoice.tier}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Period End</span>
                    <p className="text-white">
                      {selectedInvoice.period_end 
                        ? new Date(selectedInvoice.period_end).toLocaleDateString()
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded-xl p-4">
                <div className="flex gap-3">
                  <AlertCircle className="h-5 w-5 text-[#3B82F6] flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-gray-300">
                    <p className="mb-2">
                      Refunds are prorated based on time remaining in your billing period.
                    </p>
                    <p>
                      All refund requests require admin approval. Approved refunds typically 
                      take 5-10 business days to appear.
                    </p>
                  </div>
                </div>
              </div>

              {/* Reason Input */}
              <div>
                <label className="text-gray-300 text-sm mb-2 block">
                  Reason for Refund <span className="text-red-400">*</span>
                </label>
                <Textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="Please describe why you're requesting a refund..."
                  className="bg-[#1e2a45] border-[#2d3a56] text-white min-h-[100px]"
                  data-testid="refund-reason-input"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setSelectedInvoice(null)}
                  className="border-[#1e2a45] text-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitRefund}
                  disabled={submitting || !refundReason.trim()}
                  className="bg-[#F97316] hover:bg-[#EA580C] text-white"
                  data-testid="submit-refund-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Submit Request
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingPage;
